import * as React from "react";
import { Alert, Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

import type { OAuthPendingRedirect } from "@/lib/oauth/flow";
import { clearPendingRedirect, completeInput, parseCallback, readPendingRedirect, relayResult } from "@/lib/oauth/flow";
import { oauthErrorMessage, useCompleteOAuth } from "@/hooks/api/use-oauth";

type CallbackState =
	| { kind: "working" }
	/** A tab took the result; this popup only stays open if the browser refused to close it. */
	| { kind: "handed-over" }
	/** Nothing is waiting for the result: the flow was cancelled, expired, or the page was reloaded. */
	| { kind: "unclaimed" }
	| { kind: "failed"; message: string; returnTo: string };

/** The page the OAuth provider sends the browser back to. How the flow works: `@/lib/oauth/flow`. */
export function OAuthCallback(): React.JSX.Element {
	const navigate = useNavigate();
	const { mutateAsync: complete } = useCompleteOAuth();
	const [state, setState] = React.useState<CallbackState>({ kind: "working" });
	const hasStarted = React.useRef(false);

	React.useEffect(() => {
		// `state` is single-use on the backend, so this must never run twice.
		if (hasStarted.current) return;
		hasStarted.current = true;

		const result = parseCallback(globalThis.location.search);
		const pending = readPendingRedirect();

		const completeInThisTab = async ({ browserProof, returnTo }: OAuthPendingRedirect) => {
			try {
				await complete(completeInput(result, browserProof));
				globalThis.location.replace(returnTo);
			} catch (error) {
				setState({ kind: "failed", message: oauthErrorMessage(error), returnTo });
			} finally {
				// Removed only now: a reload while `complete` runs must still find it, and end on an error with
				// a way back rather than on a page that takes itself for a popup.
				clearPendingRedirect();
			}
		};

		const handOverToOpener = async () => {
			if (await relayResult(result)) {
				globalThis.close();
				setState({ kind: "handed-over" });
			} else {
				setState({ kind: "unclaimed" });
			}
		};

		if (pending) {
			void completeInThisTab(pending);
		} else {
			void handOverToOpener();
		}
	}, [complete, navigate]);

	return (
		<Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
			<Stack spacing={2} alignItems="center" sx={{ maxWidth: 420, width: "100%" }}>
				{state.kind === "working" && (
					<>
						<CircularProgress />
						<Typography color="text.secondary">Connecting your account…</Typography>
					</>
				)}

				{state.kind === "handed-over" && (
					<Typography color="text.secondary" textAlign="center">
						Done. You can close this window.
					</Typography>
				)}

				{state.kind === "unclaimed" && (
					<>
						<Alert severity="warning" sx={{ width: "100%" }}>
							This connection request is no longer active. Go back to the app and click Connect again.
						</Alert>
						<Button variant="contained" onClick={() => navigate("/", { replace: true })}>
							Back to the app
						</Button>
					</>
				)}

				{state.kind === "failed" && (
					<>
						<Alert severity="error" sx={{ width: "100%" }}>
							{state.message}
						</Alert>
						<Button variant="contained" onClick={() => globalThis.location.replace(state.returnTo)}>
							Go back and try again
						</Button>
					</>
				)}
			</Stack>
		</Box>
	);
}
